import React, { useState, useEffect, useRef } from 'react'
import './BenchmarkAnalysis.css'

/**
 * 벤치마킹 채널 분석 컴포넌트 (DataForSEO API 활용)
 */
function BenchmarkAnalysis({ 
  youtubeUrl: propsYoutubeUrl,
  onYoutubeUrlChange: propsOnYoutubeUrlChange,
  onYoutubeUrlSubmit: propsOnYoutubeUrlSubmit,
  isLoadingMetadata: propsIsLoadingMetadata,
  onVideoSelect
}) {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedChannel, setSelectedChannel] = useState(null)
  const [analysisData, setAnalysisData] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [selectedVideo, setSelectedVideo] = useState(null) // YouTube 비디오 상태 추가
  const [youtubeUrl, setYoutubeUrl] = useState('') // YouTube URL 입력 상태
  const [videoMetadata, setVideoMetadata] = useState(null) // 비디오 메타데이터
  const [isLoadingMetadata, setIsLoadingMetadata] = useState(false) // 메타데이터 로딩 상태
  const lastVideoIdRef = useRef(null) // 마지막으로 로드한 비디오 ID 추적
  
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'
  
  // YouTube URL에서 비디오 ID 추출 함수
  const extractVideoId = (url) => {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /^([a-zA-Z0-9_-]{11})$/ // 비디오 ID만 입력한 경우
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) {
        return match[1];
      }
    }
    return null;
  };
  
  // YouTube 메타데이터 가져오기
  const fetchVideoMetadata = async (url) => {
    console.log('메타데이터 가져오기 시작:', url);
    setIsLoadingMetadata(true);
    try {
      const requestBody = {
        url: url,
        extract_formats: false,
        extract_subtitles: false  // 자막은 별도로 처리
      };
      console.log('메타데이터 API 요청:', requestBody);
      
      const apiUrl = `${API_BASE_URL}/api/ytdlp/extract-info`;
      console.log('API URL:', apiUrl);
      
      // Add timeout to prevent hanging
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30초 타임아웃
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      console.log('메타데이터 API 응답 상태:', response.status);
      console.log('응답 headers:', response.headers);
      
      if (response.ok) {
        const data = await response.json();
        console.log('메타데이터 추출 성공:', data);
        setVideoMetadata(data);
        return data;
      } else {
        const errorText = await response.text();
        console.error('메타데이터 추출 실패:', response.status, errorText);
        return null;
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.error('메타데이터 API 타임아웃 (30초 초과)');
      } else {
        console.error('메타데이터 API 오류:', error);
      }
      return null;
    } finally {
      setIsLoadingMetadata(false);
    }
  };
  
  
  // 영상 다운로드 함수
  const downloadVideo = async (video, quality = 'best') => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/ytdlp/download`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          url: `https://youtube.com/watch?v=${video.id}`,
          quality: quality
        })
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${video.title || video.id}.mp4`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        alert('영상 다운로드가 완료되었습니다!');
      } else {
        const errorData = await response.json();
        alert(`다운로드 실패: ${errorData.error || '알 수 없는 오류'}`);
      }
    } catch (error) {
      console.error('영상 다운로드 오류:', error);
      alert('다운로드 중 오류가 발생했습니다.');
    }
  };

  // 오디오 추출 함수
  const extractAudio = async (video, format = 'mp3') => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/ytdlp/extract-audio`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          url: `https://youtube.com/watch?v=${video.id}`,
          format: format
        })
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${video.title || video.id}.${format}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        alert('오디오 추출이 완료되었습니다!');
      } else {
        const errorData = await response.json();
        alert(`오디오 추출 실패: ${errorData.error || '알 수 없는 오류'}`);
      }
    } catch (error) {
      console.error('오디오 추출 오류:', error);
      alert('오디오 추출 중 오류가 발생했습니다.');
    }
  };

  // YouTube URL 처리 함수
  const handleYoutubeUrlSubmit = async () => {
    console.log('handleYoutubeUrlSubmit called with URL:', youtubeUrl);
    const videoId = extractVideoId(youtubeUrl);
    console.log('Extracted video ID:', videoId);
    if (videoId) {
      // 기본 비디오 정보 설정
      const videoUrl = youtubeUrl.includes('youtube.com') || youtubeUrl.includes('youtu.be') 
        ? youtubeUrl 
        : `https://youtube.com/watch?v=${videoId}`;
      
      const videoInfo = {
        id: videoId,
        url: videoUrl
      };
      
      lastVideoIdRef.current = videoId; // 수동 URL 입력 시에도 lastVideoIdRef 업데이트
      setSelectedVideo(videoInfo);
      if (onVideoSelect) onVideoSelect(videoInfo);
      localStorage.setItem('selectedYouTubeVideo', JSON.stringify(videoInfo));
      
      // 메타데이터 가져오기
      const metadata = await fetchVideoMetadata(videoUrl);
      if (metadata) {
        // 메타데이터로 비디오 정보 업데이트
        const enrichedVideoInfo = {
          ...videoInfo,
          title: metadata.title,
          channel: metadata.uploader,
          viewCount: metadata.view_count,
          likeCount: metadata.like_count,
          duration: metadata.duration,
          description: metadata.description,
          uploadDate: metadata.upload_date,
          thumbnail: metadata.thumbnail,
          tags: metadata.tags,
          chapters: metadata.chapters
        };
        setSelectedVideo(enrichedVideoInfo);
        setVideoMetadata(metadata); // videoMetadata 상태도 설정
        if (onVideoSelect) onVideoSelect(enrichedVideoInfo);
        localStorage.setItem('selectedYouTubeVideo', JSON.stringify(enrichedVideoInfo));
      }
    } else {
      alert('올바른 YouTube URL을 입력해주세요.');
    }
  };
  
  // 이 useEffect는 제거 - 모든 초기화 로직은 아래 checkStoredVideo에서 처리

  // 카테고리 옵션
  const categories = [
    { value: 'all', label: '전체' },
    { value: 'education', label: '교육' },
    { value: 'entertainment', label: '엔터테인먼트' },
    { value: 'gaming', label: '게임' },
    { value: 'tech', label: '기술' },
    { value: 'vlog', label: '브이로그' },
    { value: 'music', label: '음악' },
    { value: 'cooking', label: '요리' },
    { value: 'beauty', label: '뷰티' }
  ]

  // 더미 데이터 (실제로는 DataForSEO API 호출)
  const dummyChannels = [
    {
      id: 1,
      name: '테크 리뷰어',
      subscribers: 450000,
      videos: 523,
      avgViews: 85000,
      engagement: 4.2,
      category: 'tech',
      thumbnail: '🎥',
      growthRate: 15.3,
      uploadFrequency: '주 3회'
    },
    {
      id: 2,
      name: '일상 브이로거',
      subscribers: 280000,
      videos: 892,
      avgViews: 45000,
      engagement: 6.8,
      category: 'vlog',
      thumbnail: '📹',
      growthRate: 12.1,
      uploadFrequency: '매일'
    },
    {
      id: 3,
      name: '게임 마스터',
      subscribers: 1250000,
      videos: 1456,
      avgViews: 250000,
      engagement: 5.5,
      category: 'gaming',
      thumbnail: '🎮',
      growthRate: 22.7,
      uploadFrequency: '주 5회'
    }
  ]

  // 채널 검색 핸들러
  const handleSearch = async () => {
    if (!searchQuery.trim()) return
    
    setIsLoading(true)
    try {
      // DataForSEO API 호출 (예시)
      // const response = await fetch('/api/dataforseo/youtube/search', {
      //   method: 'POST',
      //   body: JSON.stringify({ query: searchQuery, category: selectedCategory })
      // })
      
      // 더미 데이터 사용
      setTimeout(() => {
        setAnalysisData({
          channels: dummyChannels.filter(ch => 
            selectedCategory === 'all' || ch.category === selectedCategory
          )
        })
        setIsLoading(false)
      }, 1000)
    } catch (error) {
      console.error('Search failed:', error)
      setIsLoading(false)
    }
  }

  // 채널 상세 분석
  const analyzeChannel = async (channel) => {
    setSelectedChannel(channel)
    setIsLoading(true)
    
    try {
      // DataForSEO API로 상세 분석
      // const response = await fetch('/api/dataforseo/youtube/analyze', {
      //   method: 'POST',
      //   body: JSON.stringify({ channelId: channel.id })
      // })
      
      // 더미 상세 데이터
      setTimeout(() => {
        setSelectedChannel({
          ...channel,
          topVideos: [
            { title: '인기 영상 1', views: 520000, likes: 25000, ctr: 12.5 },
            { title: '인기 영상 2', views: 380000, likes: 18000, ctr: 10.2 },
            { title: '인기 영상 3', views: 290000, likes: 15000, ctr: 9.8 }
          ],
          keywords: [
            { keyword: '리뷰', count: 145, trend: 'up' },
            { keyword: '언박싱', count: 89, trend: 'stable' },
            { keyword: '비교', count: 67, trend: 'up' }
          ],
          bestUploadTime: '오후 7-9시',
          avgVideoLength: '12분 30초',
          thumbnailStyle: '텍스트 오버레이 + 얼굴',
          titlePattern: '[제품명] 리뷰 | 솔직 후기',
          hashtagStrategy: '#리뷰 #언박싱 #테크 (5-8개 사용)'
        })
        setIsLoading(false)
      }, 1500)
    } catch (error) {
      console.error('Analysis failed:', error)
      setIsLoading(false)
    }
  }

  // 사이드바와 통신을 위한 이벤트 발생
  useEffect(() => {
    // 사이드바에서 URL이 변경되면 로컬 상태도 업데이트
    if (propsYoutubeUrl !== undefined && propsYoutubeUrl !== youtubeUrl) {
      setYoutubeUrl(propsYoutubeUrl);
    }
  }, [propsYoutubeUrl]);

  // 로컬 handleYoutubeUrlSubmit를 사이드바와 공유
  useEffect(() => {
    // 항상 window에 핸들러 설정
    window.benchmarkHandleSubmit = handleYoutubeUrlSubmit;
    
    return () => {
      // 클린업 시 제거
      delete window.benchmarkHandleSubmit;
    };
  }, [handleYoutubeUrlSubmit]);
  
  // localStorage에서 선택된 비디오 확인하고 URL 업데이트
  useEffect(() => {
    let debounceTimer = null;
    
    const checkStoredVideo = () => {
      // 디바운싱 - 연속적인 호출을 방지
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      
      debounceTimer = setTimeout(() => {
        const storedVideo = localStorage.getItem('selectedYouTubeVideo');
        
        // localStorage에서 비디오 확인
        if (storedVideo) {
          const video = JSON.parse(storedVideo);
          if (video.id && video.id !== lastVideoIdRef.current) {
            console.log('새 비디오 로드:', video.id);
            lastVideoIdRef.current = video.id;
            const videoUrl = `https://youtube.com/watch?v=${video.id}`;
            setYoutubeUrl(videoUrl);
            // 사이드바의 URL도 업데이트
            if (propsOnYoutubeUrlChange) {
              propsOnYoutubeUrlChange(videoUrl);
            }
            setSelectedVideo(video);
            if (onVideoSelect) onVideoSelect(video);
            
            // 메타데이터가 없으면 가져오기
            if (!video.title || !video.channel) {
              fetchVideoMetadata(videoUrl).then(metadata => {
                if (metadata) {
                  const enrichedVideoInfo = {
                    ...video,
                    title: metadata.title,
                    channel: metadata.uploader,
                    viewCount: metadata.view_count,
                    likeCount: metadata.like_count,
                    duration: metadata.duration,
                    description: metadata.description,
                    uploadDate: metadata.upload_date,
                    thumbnail: metadata.thumbnail,
                    tags: metadata.tags,
                    chapters: metadata.chapters
                  };
                  setSelectedVideo(enrichedVideoInfo);
                  setVideoMetadata(metadata);
                  if (onVideoSelect) onVideoSelect(enrichedVideoInfo);
                  localStorage.setItem('selectedYouTubeVideo', JSON.stringify(enrichedVideoInfo));
                }
              });
            }
          }
        } else {
          // URL 파라미터에서 videoId 확인 (localStorage에 없는 경우)
          const urlParams = new URLSearchParams(window.location.search);
          const videoId = urlParams.get('videoId');
          if (videoId && videoId !== lastVideoIdRef.current) {
            console.log('URL 파라미터에서 비디오 로드:', videoId);
            lastVideoIdRef.current = videoId;
            const videoUrl = `https://youtube.com/watch?v=${videoId}`;
            const basicVideoInfo = { id: videoId };
            setSelectedVideo(basicVideoInfo);
            if (onVideoSelect) onVideoSelect(basicVideoInfo);
            setYoutubeUrl(videoUrl);
            
            // 메타데이터 가져오기
            fetchVideoMetadata(videoUrl).then(metadata => {
              if (metadata) {
                const enrichedVideoInfo = {
                  id: videoId,
                  title: metadata.title,
                  channel: metadata.uploader,
                  viewCount: metadata.view_count,
                  likeCount: metadata.like_count,
                  duration: metadata.duration,
                  description: metadata.description,
                  uploadDate: metadata.upload_date,
                  thumbnail: metadata.thumbnail,
                  tags: metadata.tags,
                  chapters: metadata.chapters
                };
                setSelectedVideo(enrichedVideoInfo);
                setVideoMetadata(metadata);
                if (onVideoSelect) onVideoSelect(enrichedVideoInfo);
                localStorage.setItem('selectedYouTubeVideo', JSON.stringify(enrichedVideoInfo));
              }
            });
          }
        }
      }, 100); // 100ms 디바운스
    };
    
    // 초기 확인
    checkStoredVideo();
    
    // storage 이벤트 리스너 (다른 탭/창에서 변경 감지)
    window.addEventListener('storage', checkStoredVideo);
    
    // 커스텀 이벤트 리스너 (같은 창에서 변경 감지)
    window.addEventListener('youtubeVideoSelected', checkStoredVideo);
    
    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      window.removeEventListener('storage', checkStoredVideo);
      window.removeEventListener('youtubeVideoSelected', checkStoredVideo);
    };
  }, [propsOnYoutubeUrlChange]);

  return (
    <div className="benchmark-analysis">
      {/* URL 입력 섹션은 사이드바로 이동했으므로 제거 */}
      
      {/* 메인 컨텐츠 영역 - 영상과 자막을 나란히 표시 */}
      {selectedVideo && (
        <div className="youtube-content-wrapper">
          {/* 왼쪽: YouTube 비디오 임베드 */}
          <div className="youtube-embed-section">
          <div className="youtube-embed-header">
            <h3>선택된 YouTube 영상</h3>
          </div>
          
          <div className="youtube-player-container">
            <iframe
              className="youtube-player"
              src={`https://www.youtube.com/embed/${selectedVideo.id}`}
              title={selectedVideo.title || 'YouTube video player'}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
          
          {/* 메타데이터 로딩 중 표시 */}
          {isLoadingMetadata && !selectedVideo.title && (
            <div className="video-info">
              <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                <div className="loading-spinner" style={{ 
                  margin: '0 auto 10px',
                  width: '30px',
                  height: '30px',
                  border: '3px solid #333',
                  borderTopColor: '#667eea',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
                메타데이터를 가져오는 중...
              </div>
            </div>
          )}
          
          {/* 메타데이터 표시 */}
          {selectedVideo.title && (
            <div className="video-info">
              <h4>{selectedVideo.title}</h4>
              <div className="video-stats">
                {selectedVideo.channel && <span className="channel-name">📺 {selectedVideo.channel}</span>}
                {selectedVideo.viewCount && <span>👁️ 조회수: {selectedVideo.viewCount?.toLocaleString()}</span>}
                {selectedVideo.likeCount && <span>👍 좋아요: {selectedVideo.likeCount?.toLocaleString()}</span>}
                {selectedVideo.duration && <span>⏱️ 길이: {Math.floor(selectedVideo.duration / 60)}:{(selectedVideo.duration % 60).toString().padStart(2, '0')}</span>}
              </div>
              {selectedVideo.uploadDate && (
                <div className="upload-date">
                  업로드: {selectedVideo.uploadDate.replace(/(\d{4})(\d{2})(\d{2})/, '$1년 $2월 $3일')}
                </div>
              )}
              {selectedVideo.tags && selectedVideo.tags.length > 0 && (
                <div className="video-tags">
                  {selectedVideo.tags.slice(0, 5).map((tag, index) => (
                    <span key={index} className="tag-badge">#{tag}</span>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* 상세 메타데이터 (접을 수 있는 섹션) */}
          {videoMetadata && (
            <details className="metadata-details">
              <summary>📊 상세 분석 정보</summary>
              <div className="metadata-content">
                {videoMetadata.description && (
                  <div className="description-section">
                    <h5>📝 설명</h5>
                    <p className="video-description">{videoMetadata.description.substring(0, 300)}...</p>
                  </div>
                )}
                {videoMetadata.chapters && videoMetadata.chapters.length > 0 && (
                  <div className="chapters-section">
                    <h5>📑 챕터</h5>
                    <ul className="chapters-list">
                      {videoMetadata.chapters.map((chapter, index) => (
                        <li key={index}>
                          {Math.floor(chapter.start_time / 60)}:{(chapter.start_time % 60).toString().padStart(2, '0')} - {chapter.title}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </details>
          )}
          
        </div>
        </div>
      )}
      
      {/* 상세 분석 결과 - 채널 검색 기능 제거, 선택된 YouTube 영상이 없을 때만 표시 */}
      {!selectedVideo && selectedChannel && selectedChannel.topVideos && (
        <div className="detailed-analysis">
          <h3>{selectedChannel.name} 상세 분석</h3>
          
          {/* 성공 패턴 분석 */}
          <div className="success-patterns">
            <h4>🎯 성공 패턴 분석</h4>
            <div className="pattern-grid">
              <div className="pattern-card">
                <div className="pattern-icon">⏰</div>
                <div className="pattern-content">
                  <h5>최적 업로드 시간</h5>
                  <p>{selectedChannel.bestUploadTime}</p>
                </div>
              </div>
              <div className="pattern-card">
                <div className="pattern-icon">⏱️</div>
                <div className="pattern-content">
                  <h5>평균 영상 길이</h5>
                  <p>{selectedChannel.avgVideoLength}</p>
                </div>
              </div>
              <div className="pattern-card">
                <div className="pattern-icon">🎨</div>
                <div className="pattern-content">
                  <h5>썸네일 스타일</h5>
                  <p>{selectedChannel.thumbnailStyle}</p>
                </div>
              </div>
              <div className="pattern-card">
                <div className="pattern-icon">📝</div>
                <div className="pattern-content">
                  <h5>제목 패턴</h5>
                  <p>{selectedChannel.titlePattern}</p>
                </div>
              </div>
            </div>
          </div>

          {/* 인기 영상 분석 */}
          <div className="top-videos-analysis">
            <h4>🔥 인기 영상 TOP 3</h4>
            <div className="videos-table">
              <div className="table-header">
                <span>영상 제목</span>
                <span>조회수</span>
                <span>좋아요</span>
                <span>CTR</span>
              </div>
              {selectedChannel.topVideos.map((video, idx) => (
                <div key={idx} className="table-row">
                  <span className="video-title">
                    <span className="rank">#{idx + 1}</span> {video.title}
                  </span>
                  <span>{video.views.toLocaleString()}</span>
                  <span>{video.likes.toLocaleString()}</span>
                  <span className="ctr-value">{video.ctr}%</span>
                </div>
              ))}
            </div>
          </div>

          {/* 키워드 전략 */}
          <div className="keyword-strategy">
            <h4>🔍 주요 키워드 전략</h4>
            <div className="keywords-container">
              <div className="keyword-list">
                {selectedChannel.keywords.map((kw, idx) => (
                  <div key={idx} className="keyword-item">
                    <span className="keyword-text">{kw.keyword}</span>
                    <span className="keyword-count">{kw.count}회</span>
                    <span className={`keyword-trend ${kw.trend}`}>
                      {kw.trend === 'up' && '↑'}
                      {kw.trend === 'down' && '↓'}
                      {kw.trend === 'stable' && '→'}
                    </span>
                  </div>
                ))}
              </div>
              <div className="hashtag-strategy">
                <h5>해시태그 전략</h5>
                <p>{selectedChannel.hashtagStrategy}</p>
              </div>
            </div>
          </div>

          {/* 적용 가능한 인사이트 */}
          <div className="actionable-insights">
            <h4>💡 적용 가능한 인사이트</h4>
            <div className="insight-list">
              <div className="insight-item">
                <span className="insight-icon">✅</span>
                <span>이 채널의 업로드 시간대를 참고하여 콘텐츠 발행 일정을 조정해보세요</span>
              </div>
              <div className="insight-item">
                <span className="insight-icon">✅</span>
                <span>썸네일에 텍스트 오버레이를 활용하면 CTR 향상에 도움이 될 수 있습니다</span>
              </div>
              <div className="insight-item">
                <span className="insight-icon">✅</span>
                <span>12-15분 길이의 영상이 이 카테고리에서 좋은 성과를 보입니다</span>
              </div>
              <div className="insight-item">
                <span className="insight-icon">✅</span>
                <span>제목에 구체적인 키워드를 포함하여 검색 노출을 높이세요</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 로딩 상태 */}
      {isLoading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <p>채널 데이터를 분석하고 있습니다...</p>
        </div>
      )}
    </div>
  )
}

export default BenchmarkAnalysis